'use client';

import React, { useMemo } from 'react';

import { useAutoplay } from '../../../../../../hooks/useAutoplay';
import { useCarousel } from '../../../../../../hooks/useCarousel';
import { useSwipeGestures } from '../../../../../../hooks/useSwipeGestures';
import { Activity } from '../../../../../../services/activities.service';
import { CarouselSlide } from './CarouselSlide';
import { DotIndicators } from './DotIndicators';

interface MobileActivityImageProps {
  activity: Activity;
}

/**
 * Mobile-specific activity image component with carousel functionality
 * Supports swipe navigation, autoplay, and dot indicators
 * Optimized for mobile viewports and touch interactions
 */
export function MobileActivityImage({ activity }: MobileActivityImageProps) {
  // Combine primary image with additional media
  const images = useMemo(() => {
    const allImages = [activity.imageUrl];
    if (activity.mediaUrls && activity.mediaUrls.length > 0) {
      allImages.push(...activity.mediaUrls);
    }
    return allImages;
  }, [activity.imageUrl, activity.mediaUrls]);

  const showCarousel = images.length > 1;

  // Carousel state management - always call hook, even if not used
  const { currentSlide, goToSlide, nextSlide, previousSlide } = useCarousel({
    totalSlides: Math.max(images.length, 1) // Ensure at least 1 to avoid issues
  });

  // Autoplay functionality - always call hook
  const { pause, resume } = useAutoplay({
    isEnabled: showCarousel,
    interval: 6000,
    onNext: nextSlide
  });

  // Swipe gesture handling - always call hook
  const swipeHandlers = useSwipeGestures({
    onSwipeLeft: showCarousel ? nextSlide : undefined,
    onSwipeRight: showCarousel ? previousSlide : undefined,
    onSwipeStart: showCarousel ? pause : undefined,
    onSwipeEnd: showCarousel
      ? () => {
          // Resume autoplay after a short delay
          setTimeout(resume, 1000);
        }
      : undefined,
    threshold: 50,
    velocityThreshold: 0.3
  });

  // Always return the same structure - no early returns after hooks
  return (
    <div className="relative w-screen h-[200px] overflow-hidden">
      {showCarousel ? (
        <>
          <div {...swipeHandlers} onMouseEnter={pause} onMouseLeave={resume} className="w-full h-full">
            {/* Carousel container */}
            <div
              className="flex h-full transition-transform duration-300 ease-in-out"
              style={{
                transform: `translateX(-${currentSlide * 100}vw)`,
                width: `${images.length * 100}vw`
              }}
            >
              {images.map((imageUrl, index) => (
                <CarouselSlide
                  key={`${imageUrl}-${index}`}
                  src={imageUrl}
                  alt={`${activity.title} - Image ${index + 1}`}
                  isActive={index === currentSlide}
                  priority={index === 0} // Prioritize first image
                />
              ))}
            </div>
          </div>

          {/* Dot indicators */}
          <DotIndicators
            totalSlides={images.length}
            currentSlide={currentSlide}
            onDotClick={(index) => {
              pause();
              goToSlide(index);
              setTimeout(resume, 2000); // Resume after 2 seconds
            }}
          />
        </>
      ) : (
        <CarouselSlide src={activity.imageUrl} alt={activity.title} isActive={true} priority={true} />
      )}
    </div>
  );
}
